<template>
  <div class="admin-page event-index">
    <!-- 头部搜索与操作栏 -->
    <div class="admin-filter-bar">
      <admin-filters class="toolbar-left" :active="!!query.title || !!(query.eventDateRange && query.eventDateRange.length)">
        <a-input
          v-model="query.title"
          placeholder="搜索赛事名称"
          style="width:240px; border-radius:6px;"
          allow-clear
          @pressEnter="loadData"
        />
        <a-range-picker
          v-model="query.eventDateRange"
          value-format="YYYY-MM-DD"
          style="width:260px;"
          allow-clear
          @change="loadData"
        />
        <a-button type="primary" icon="search" @click="loadData">查询</a-button>
      </admin-filters>
      <div class="toolbar-right">
        <a-button type="primary" size="large" icon="plus" @click="handleAdd">
          创建赛事
        </a-button>
      </div>
    </div>

    <!-- 赛事卡片列表 -->
    <a-spin :spinning="loading">
      <div v-if="data && data.length > 0">
        <a-row :gutter="[20, 20]">
          <a-col v-for="item in data" :key="item.id" :xs="24" :sm="12" :md="12" :lg="8" :xxl="6">
            <div class="event-card">
              <!-- 卡片头部信息 -->
              <div class="event-card-header">
                <div class="event-title-wrapper">
                  <h3 class="event-title" :title="item.title">{{ item.title }}</h3>
                  <div class="event-badges">
                    <a-tag v-if="item.isRecommended === 1" color="gold" class="event-status-tag">推荐</a-tag>
                    <a-tag v-if="item.isHidden === 1" color="default" class="event-status-tag">隐藏</a-tag>
                    <a-tag :color="statusColor(item.status)" class="event-status-tag">
                      {{ !registrationEnabled(item) && [1, 4].includes(item.status) ? rt('upcoming') : statusText(item.status) }}
                    </a-tag>
                  </div>
                </div>
                <div class="event-meta">
                  <div class="meta-item">
                    <a-icon type="calendar" />
                    <span>举办时间：{{ formatEventDateRange(item) }}</span>
                  </div>
                  <div class="meta-item">
                    <a-icon type="environment" />
                    <span>地点：{{ item.location || '待定' }}</span>
                  </div>
                  <div v-if="registrationEnabled(item)" class="meta-item">
                    <a-icon type="appstore" />
                    <span>船型：{{ boatSeatLayoutText(item) }}</span>
                  </div>
                  <div v-if="registrationEnabled(item)" class="meta-item">
                    <a-icon type="team" />
                    <span>参赛人数：{{ item.minParticipants || 1 }}-{{ item.maxParticipants || layoutSeatCount(boatRowsForPreview(item)) }}人</span>
                  </div>
                </div>
              </div>

              <!-- 卡片数据统计 -->
              <div class="event-card-stats">
                <div class="stat-box">
                  <div class="stat-val">{{ item.teamCount || 0 }}</div>
                  <div class="stat-label">{{ rt('entries') }}</div>
                </div>
                <div class="stat-box">
                  <div class="stat-val">{{ item.categoryCount || 0 }}</div>
                  <div class="stat-label">组别</div>
                </div>
                <div class="stat-box">
                  <div class="stat-val">{{ item.roundCount || 0 }}</div>
                  <div class="stat-label">比赛组</div>
                </div>
              </div>

              <!-- 卡片底部按钮 -->
              <div class="event-card-actions">
                <a-button type="primary" icon="appstore" class="action-btn-main" @click="handleEnterEvent(item)">
                  进入赛事
                </a-button>
                <div class="event-card-secondary-actions">
                  <a-tooltip :title="item.isHidden === 1 ? '隐藏赛事不会在小程序展示，请先取消隐藏' : '预览并下载观众扫码使用的赛事小程序码'">
                    <span class="secondary-action-wrap">
                      <a-button icon="share-alt" class="action-btn-share" :disabled="item.isHidden === 1" @click="handleShareCode(item)">
                        分享码
                      </a-button>
                    </span>
                  </a-tooltip>
                  <a-button icon="edit" class="action-btn-edit" @click="handleEdit(item)">
                    编辑
                  </a-button>
                  <a-button icon="delete" class="action-btn-delete" @click="$refs.recycle.open(item.id,'move')">{{ recycleText($adminLocale,'move') }}</a-button>
                </div>
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- 分页 -->
        <div class="pagination-container">
          <a-pagination
            v-model="pagination.current"
            :pageSize="pagination.pageSize"
            :total="pagination.total"
            @change="handlePageChange"
            show-less-items
          />
        </div>
      </div>
      <div v-else style="padding: 80px 0; background: var(--jk-surface, #fff); border-radius: 12px; text-align: center;">
        <a-empty description="暂无赛事数据" />
      </div>
    </a-spin>

    <event-recycle-dialog ref="recycle" @updated="loadData" />
    <!-- 新增/编辑赛事弹窗 -->
    <a-modal v-model="visible" :title="form.id ? '编辑赛事' : '新增赛事'" @ok="handleOk" :confirm-loading="saving" :closable="!saving" :mask-closable="!saving" width="760px" destroyOnClose>
      <a-form-model ref="formRef" :model="form" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-model-item :label="rt('registration')">
          <a-switch :checked="form.registrationEnabled === 1" @change="changeRegistration" />
          <div class="upload-tip">{{ rt('registrationHelp') }}</div>
        </a-form-model-item>
        <a-form-model-item :label="rt('claims')">
          <a-switch :checked="form.claimsEnabled === 1" @change="form.claimsEnabled = $event ? 1 : 0" />
          <div class="upload-tip">{{ rt('claimsHelp') }}</div>
        </a-form-model-item>
        <single-source-content ref="eventContent" :value="form" :fields="eventContentFields" kind="event" :event-id="form.id || null" />
        <a-form-model-item label="赛事时间" prop="eventDateRange">
          <a-range-picker v-model="form.eventDateRange" value-format="YYYY-MM-DD" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item v-if="form.registrationEnabled === 1" label="报名截止" prop="signupDeadline">
          <a-date-picker v-model="form.signupDeadline" show-time value-format="YYYY-MM-DD HH:mm:ss" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item v-if="form.registrationEnabled === 1" label="默认船型">
          <div class="boat-thumbnail" role="button" tabindex="0" @click="openBoatLayoutEditor">
            <div
              v-for="(seatCount, rowIndex) in boatRowsForPreview(form)"
              :key="rowIndex"
              class="boat-thumbnail-row"
            >
              <span v-for="seatIndex in seatCount" :key="seatIndex" class="boat-thumbnail-seat"></span>
            </div>
          </div>
          <div class="boat-thumbnail-summary">{{ boatSeatLayoutText(form) }}，新增组别将继承此布局；同一赛事的特殊组别可单独设置。</div>
        </a-form-model-item>
        <a-form-model-item v-if="form.registrationEnabled === 1" label="参赛人数">
          <div style="display:flex; align-items:center; gap:12px;">
            <a-input-number v-model="form.minParticipants" :min="1" :max="form.maxParticipants || layoutSeatCount(boatRowsForPreview(form))" />
            <span>至</span>
            <a-input-number v-model="form.maxParticipants" :min="form.minParticipants || 1" :max="layoutSeatCount(boatRowsForPreview(form))" />
            <span>人</span>
          </div>
          <div class="upload-tip">人数可以少于座舱总数，最多不能超过当前船型座位数。</div>
        </a-form-model-item>
        <a-form-model-item v-if="form.registrationEnabled === 1" label="默认替补人数">
          <div style="display:flex; align-items:center; gap:12px;">
            <a-input-number v-model="form.maxAlternateParticipants" :min="0" />
            <span>人</span>
          </div>
          <div class="upload-tip">新增组别继承此限额；特殊组别可单独设置，已有报名的组别不会自动改变。</div>
        </a-form-model-item>
        <a-form-model-item label="展示设置">
          <div class="display-setting">
            <span class="setting-label">首页推荐</span>
            <a-switch
              :checked="form.isRecommended === 1"
              :disabled="form.isHidden === 1"
              @change="handleRecommendedChange"
            />
            <span class="setting-label">后台隐藏</span>
            <a-switch
              :checked="form.isHidden === 1"
              @change="handleHiddenChange"
            />
          </div>
          <div class="upload-tip">隐藏赛事仅后台记录，不会在小程序赛事列表、首页推荐和详情中展示。</div>
        </a-form-model-item>
        <a-form-model-item label="秩序册文件">
          <a-upload
            :customRequest="uploadBrochure"
            :showUploadList="false"
            accept=".pdf"
          >
            <a-button :loading="uploadingBrochure">上传秩序册 PDF</a-button>
          </a-upload>
          <div class="upload-tip">仅支持 PDF，大小不超过 20MB。</div>
          <div v-if="form.brochureUrl" class="upload-preview">
            <a :href="resolveFileUrl(form.brochureUrl)" target="_blank">查看当前秩序册</a>
          </div>
        </a-form-model-item>
        <a-form-model-item label="封面图文件">
          <a-upload
            :customRequest="uploadCover"
            :showUploadList="false"
            accept=".jpg,.jpeg,.png"
          >
            <a-button :loading="uploadingCover">上传封面图</a-button>
          </a-upload>
          <div class="upload-tip">仅支持 jpg、jpeg、png，大小不超过 5MB。</div>
          <div v-if="form.coverImage" class="upload-preview">
            <img :src="resolveFileUrl(form.coverImage)" alt="赛事封面" class="cover-preview" />
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      v-model="layoutVisible"
      title="设置船型座位"
      width="560px"
      ok-text="应用"
      cancel-text="取消"
      :mask-closable="false"
      @ok="confirmBoatLayout"
    >
      <div class="layout-editor">
        <div v-for="(seatCount, rowIndex) in layoutDraft" :key="rowIndex" class="layout-editor-row">
          <span class="layout-row-index">第{{ rowIndex + 1 }}行</span>
          <div class="layout-seat-preview">
            <span v-for="seatIndex in seatCount" :key="seatIndex" class="layout-seat"></span>
          </div>
          <a-radio-group :value="seatCount" size="small" @change="updateLayoutRow(rowIndex, $event.target.value)">
            <a-radio-button :value="1">1座</a-radio-button>
            <a-radio-button :value="2">2座</a-radio-button>
          </a-radio-group>
          <a-button type="link" class="layout-delete" :disabled="layoutDraft.length === 1" @click="removeLayoutRow(rowIndex)">
            删除
          </a-button>
        </div>
        <a-button type="dashed" block :disabled="layoutDraft.length >= 30" @click="addLayoutRow">
          新增一行
        </a-button>
        <div class="layout-summary">共{{ layoutDraft.length }}行，{{ layoutSeatCount(layoutDraft) }}个座位</div>
      </div>
    </a-modal>

    <a-modal
      v-model="shareVisible"
      title="赛事观众分享码"
      :footer="null"
      width="420px"
      destroyOnClose
      @cancel="closeShareCode"
    >
      <a-spin :spinning="shareLoading">
        <div class="share-code-panel">
          <div class="share-code-title">{{ shareEvent ? shareEvent.title : '' }}</div>
          <div class="share-code-frame">
            <img v-if="shareCodeUrl" :src="shareCodeUrl" alt="赛事小程序码" class="share-code-image" />
            <a-empty v-else-if="!shareLoading" description="分享码加载失败" />
          </div>
          <div class="share-code-tip">观众使用微信扫码后，将直接进入本赛事详情页，无需先登录即可浏览公开赛事信息。</div>
          <a-button type="primary" block icon="download" :disabled="!shareCodeUrl" @click="downloadShareCode">
            下载图片
          </a-button>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import request from '@/utils/request'
import SingleSourceContent from '@/components/SingleSourceContent.vue'
import EventRecycleDialog from '@/components/EventRecycleDialog.vue'
import {recycleText} from '@/utils/event-recycle'
import { EVENT_CONTENT_FIELDS, createContentDraft, contentPayload } from '@/utils/single-source-content'
import { getDragonEventList, getDragonEventMiniProgramCode, saveDragonEvent } from '@/api'
import { normalizePublishedLocales, parsePublishedLocales } from '@/utils/content-locale'
import { resultsText, registrationEnabled } from '@/utils/results-service'

export default {
  components: { SingleSourceContent, EventRecycleDialog },
  data() {
    return {
      query: { title: '', eventDateRange: [] },
      data: [],
      loading: false,
      visible: false,
      saving: false,
      eventContentFields: EVENT_CONTENT_FIELDS,
      shareVisible: false,
      shareLoading: false,
      shareEvent: null,
      shareCodeUrl: '',
      shareCodeExtension: 'jpg',
      shareRequestSerial: 0,
      layoutVisible: false,
      layoutDraft: [2],
      uploadingBrochure: false,
      uploadingCover: false,
      uploadBaseUrl: request.defaults.baseURL.replace(/\/api$/, ''),
      form: {
        registrationEnabled: 0,
        claimsEnabled: 0,
        originalContent: {},
        eventDateRange: [],
        eventType: '',
        defaultLocale: 'zh-Hans',
        publishedLocales: ['zh-Hans'],
        brochureUrl: '',
        coverImage: '',
        boatSeatLayout: 2,
        boatSeatRows: [2],
        minParticipants: 1,
        maxParticipants: 2,
        maxAlternateParticipants: 0,
        isRecommended: 0,
        isHidden: 0
      },
      pagination: { current: 1, pageSize: 8, total: 0 },
      rules: {
        title: [{ required: true, message: '请输入赛事标题' }],
        eventDateRange: [{ required: true, message: '请选择赛事时间' }],
        signupDeadline: [{ required: true, message: '请选择报名截止时间' }]
      }
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.revokeShareCodeUrl()
  },
  methods: {
    registrationEnabled,
    rt(key) { return resultsText(this.$adminLocale, key) },
    loadData() {
      this.loading = true
      getDragonEventList({
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        title: this.query.title,
        eventStartDate: this.query.eventDateRange && this.query.eventDateRange.length ? this.query.eventDateRange[0] : undefined,
        eventEndDate: this.query.eventDateRange && this.query.eventDateRange.length ? this.query.eventDateRange[1] : undefined
      }).then(res => {
        this.data = (res.data && res.data.list) || []
        this.pagination.total = (res.data && res.data.total) || 0
      }).finally(() => {
        this.loading = false
      })
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.loadData()
    },
    handleAdd() {
      this.form = createContentDraft({
        registrationEnabled: 0,
        claimsEnabled: 0,
        eventDateRange: [],
        eventType: '',
        defaultLocale: 'zh-Hans',
        publishedLocales: ['zh-Hans', 'zh-Hant', 'en'],
        brochureUrl: '',
        coverImage: '',
        boatSeatLayout: 2,
        boatSeatRows: [2],
        minParticipants: 1,
        maxParticipants: 2,
        maxAlternateParticipants: 0,
        isRecommended: 0,
        isHidden: 0
      }, EVENT_CONTENT_FIELDS)
      this.visible = true
    },
    handleEdit(record) {
      const enabled = registrationEnabled(record)
      this.form = createContentDraft({
        ...record,
        registrationEnabled: enabled ? 1 : 0,
        claimsEnabled: record.claimsEnabled === 1 ? 1 : 0,
        defaultLocale: 'zh-Hans',
        publishedLocales: parsePublishedLocales(record.publishedLocales, 'zh-Hans'),
        brochureUrl: record.brochureUrl || '',
        coverImage: record.coverImage || '',
        boatSeatLayout: enabled ? record.boatSeatLayout || 2 : record.boatSeatLayout,
        boatSeatRows: Array.isArray(record.boatSeatRows) ? record.boatSeatRows.slice() : record.boatSeatRows,
        minParticipants: enabled ? Number(record.minParticipants) || 1 : record.minParticipants,
        maxParticipants: enabled ? Number(record.maxParticipants) || this.layoutSeatCount(this.boatRowsForPreview(record)) : record.maxParticipants,
        maxAlternateParticipants: enabled ? Number(record.maxAlternateParticipants) || 0 : record.maxAlternateParticipants,
        isRecommended: record.isRecommended === 1 ? 1 : 0,
        isHidden: record.isHidden === 1 ? 1 : 0,
        eventDateRange: this.formatEventDateRangeValue(record),
        signupDeadline: this.formatInputDateTime(record.signupDeadline)
      }, EVENT_CONTENT_FIELDS)
      this.visible = true
    },
    changeRegistration(enabled) {
      this.form.registrationEnabled = enabled ? 1 : 0
      if (!enabled) return
      if (this.form.boatSeatLayout == null) this.$set(this.form, 'boatSeatLayout', 2)
      if (this.form.minParticipants == null) this.$set(this.form, 'minParticipants', 1)
      if (this.form.maxParticipants == null) this.$set(this.form, 'maxParticipants', this.layoutSeatCount(this.boatRowsForPreview(this.form)))
      if (this.form.maxAlternateParticipants == null) this.$set(this.form, 'maxAlternateParticipants', 0)
    },
    handleOk() {
      if (this.saving) return
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        if (!this.form.eventDateRange || this.form.eventDateRange.length !== 2) {
          this.$message.warning('请选择赛事时间')
          return
        }
        const acceptsRegistration = this.form.registrationEnabled === 1
        if (acceptsRegistration && !this.form.id && (!Array.isArray(this.form.boatSeatRows) || !this.form.boatSeatRows.length)) {
          this.$message.warning('请设置船型座位')
          return
        }
        const seatCapacity = this.layoutSeatCount(this.boatRowsForPreview(this.form))
        const minParticipants = Number(this.form.minParticipants)
        const maxParticipants = Number(this.form.maxParticipants)
        const maxAlternateParticipants = Number(this.form.maxAlternateParticipants)
        if (acceptsRegistration && (!Number.isInteger(minParticipants) || !Number.isInteger(maxParticipants)
          || minParticipants < 1 || maxParticipants < minParticipants || maxParticipants > seatCapacity)) {
          this.$message.warning(`参赛人数应在1至${seatCapacity}人之间，且下限不能大于上限`)
          return
        }
        if (acceptsRegistration && (!Number.isInteger(maxAlternateParticipants) || maxAlternateParticipants < 0)) {
          this.$message.warning('替补人数不能小于0人')
          return
        }
        this.saving = true
        try {
          await this.$refs.eventContent.prepare()
          this.form.publishedLocales = normalizePublishedLocales(this.form.publishedLocales, 'zh-Hans')
          const payload = {
            ...contentPayload(this.form),
            registrationEnabled: acceptsRegistration ? 1 : 0,
            claimsEnabled: this.form.claimsEnabled === 1 ? 1 : 0,
            isRecommended: this.form.isRecommended === 1 ? 1 : 0,
            isHidden: this.form.isHidden === 1 ? 1 : 0,
            eventStartDate: this.form.eventDateRange[0],
            eventEndDate: this.form.eventDateRange[1]
          }
          delete payload.eventDateRange
          await saveDragonEvent(payload)
          this.$message.success('保存成功')
          this.visible = false
          this.loadData()
        } catch (error) {
          this.$message.warning(error.message || '保存失败，表单内容已保留')
        } finally { this.saving = false }
      })
    },
    recycleText,
    handleEnterEvent(item) {
      this.$router.push({
        path: '/dragonboat/event/workbench',
        query: { eventId: item.id }
      })
    },
    handleShareCode(item) {
      if (item.isHidden === 1) {
        this.$message.warning('隐藏赛事不能生成观众分享码，请先取消隐藏')
        return
      }
      this.revokeShareCodeUrl()
      this.shareEvent = item
      this.shareVisible = true
      this.shareLoading = true
      const requestSerial = ++this.shareRequestSerial
      getDragonEventMiniProgramCode(item.id)
        .then(async response => {
          if (requestSerial !== this.shareRequestSerial) return
          const contentType = String(response.headers['content-type'] || '').toLowerCase()
          if (!contentType.startsWith('image/')) {
            const message = await this.readBlobError(response.data)
            throw new Error(message || '分享码生成失败')
          }
          this.shareCodeExtension = contentType.includes('png') ? 'png' : 'jpg'
          this.shareCodeUrl = URL.createObjectURL(response.data)
        })
        .catch(err => {
          if (requestSerial !== this.shareRequestSerial) return
          this.$message.error(err.message || '分享码生成失败')
        })
        .finally(() => {
          if (requestSerial === this.shareRequestSerial) this.shareLoading = false
        })
    },
    async readBlobError(blob) {
      try {
        const text = typeof blob.text === 'function'
          ? await blob.text()
          : await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsText(blob)
          })
        const payload = JSON.parse(text)
        return payload.msg || ''
      } catch (error) {
        return ''
      }
    },
    downloadShareCode() {
      if (!this.shareCodeUrl || !this.shareEvent) return
      const safeTitle = String(this.shareEvent.title || `赛事-${this.shareEvent.id}`)
        .replace(/[\\/:*?"<>|]/g, '-')
      const link = document.createElement('a')
      link.href = this.shareCodeUrl
      link.download = `${safeTitle}-小程序码.${this.shareCodeExtension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    closeShareCode() {
      this.shareRequestSerial += 1
      this.shareVisible = false
      this.shareLoading = false
      this.shareEvent = null
      this.shareCodeExtension = 'jpg'
      this.revokeShareCodeUrl()
    },
    revokeShareCodeUrl() {
      if (this.shareCodeUrl) {
        URL.revokeObjectURL(this.shareCodeUrl)
        this.shareCodeUrl = ''
      }
    },
    handleRecommendedChange(checked) {
      this.$set(this.form, 'isRecommended', checked ? 1 : 0)
    },
    handleHiddenChange(checked) {
      this.$set(this.form, 'isHidden', checked ? 1 : 0)
      if (checked) {
        this.$set(this.form, 'isRecommended', 0)
      }
    },
    formatEventDateRange(item) {
      const start = item.eventStartDate || (item.eventTime ? item.eventTime.slice(0, 10) : '')
      const end = item.eventEndDate || start
      if (!start) return '待定'
      return start === end ? start : `${start} 至 ${end}`
    },
    statusColor(status) {
      const colors = {
        0: 'orange',
        1: 'blue',
        2: 'green',
        3: 'default',
        4: 'gold'
      }
      return colors[status] || 'default'
    },
    boatRowsForPreview(event) {
      if (event && Array.isArray(event.boatSeatRows) && event.boatSeatRows.length) {
        return event.boatSeatRows.map(Number).filter(item => item === 1 || item === 2)
      }
      return [Number(event && event.boatSeatLayout) === 1 ? 1 : 2]
    },
    layoutSeatCount(rows) {
      return (rows || []).reduce((total, item) => total + Number(item || 0), 0)
    },
    boatSeatLayoutText(event) {
      const rows = this.boatRowsForPreview(event)
      if (!event || !Array.isArray(event.boatSeatRows) || !event.boatSeatRows.length) {
        return Number(event && event.boatSeatLayout) === 1 ? '传统单座排列' : '传统双座排列'
      }
      return `${rows.length}行 / ${this.layoutSeatCount(rows)}座`
    },
    openBoatLayoutEditor() {
      this.layoutDraft = this.boatRowsForPreview(this.form).slice()
      this.layoutVisible = true
    },
    addLayoutRow() {
      if (this.layoutDraft.length >= 30) return
      this.layoutDraft.push(2)
    },
    removeLayoutRow(rowIndex) {
      if (this.layoutDraft.length <= 1) return
      this.layoutDraft.splice(rowIndex, 1)
    },
    updateLayoutRow(rowIndex, seatCount) {
      this.$set(this.layoutDraft, rowIndex, Number(seatCount))
    },
    confirmBoatLayout() {
      if (!this.layoutDraft.length) {
        this.$message.warning('请至少保留一行座位')
        return
      }
      const rows = this.layoutDraft.map(Number)
      this.$set(this.form, 'boatSeatRows', rows)
      this.$set(this.form, 'boatSeatLayout', rows.some(item => item === 2) ? 2 : 1)
      const seatCapacity = this.layoutSeatCount(rows)
      const maxParticipants = Math.min(Number(this.form.maxParticipants) || seatCapacity, seatCapacity)
      this.$set(this.form, 'maxParticipants', maxParticipants)
      this.$set(this.form, 'minParticipants', Math.min(Number(this.form.minParticipants) || 1, maxParticipants))
      this.layoutVisible = false
    },
    statusText(status) {
      const labels = {
        0: '准备阶段',
        1: '报名阶段',
        2: '比赛阶段',
        3: '已结束',
        4: '报名截止，待开赛'
      }
      return labels[status] || '未知'
    },
    formatEventDateRangeValue(record) {
      const start = record.eventStartDate || (record.eventTime ? record.eventTime.slice(0, 10) : '')
      const end = record.eventEndDate || start
      return start ? [start, end] : []
    },
    formatInputDateTime(value) {
      if (!value) return ''
      return String(value).length === 16 ? `${value}:00` : String(value).slice(0, 19)
    },
    resolveFileUrl(url) {
      if (!url) return ''
      return /^https?:\/\//.test(url) ? url : `${this.uploadBaseUrl}${url}`
    },
    uploadBrochure({ file, onSuccess, onError }) {
      this.uploadingBrochure = true
      this.uploadFile(file, 'dragon_event_brochure').then(fileUrl => {
        this.form.brochureUrl = fileUrl
        this.$message.success('秩序册上传成功')
        onSuccess({ fileUrl })
      }).catch(err => {
        onError(err)
      }).finally(() => {
        this.uploadingBrochure = false
      })
    },
    uploadCover({ file, onSuccess, onError }) {
      this.uploadingCover = true
      this.uploadFile(file, 'dragon_event_cover').then(fileUrl => {
        this.form.coverImage = fileUrl
        this.$message.success('封面图上传成功')
        onSuccess({ fileUrl })
      }).catch(err => {
        onError(err)
      }).finally(() => {
        this.uploadingCover = false
      })
    },
    uploadFile(file, businessType) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('businessType', businessType)
      if (this.form.id) {
        formData.append('businessId', this.form.id)
      }
      return request({
        url: '/dragonboat/event/file/upload',
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(res => res.data.fileUrl)
    }
  }
}
</script>

<style scoped>
.native-input {
  width: 100%;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 11px;
}

.content-language-panel {
  margin-bottom: 22px;
  padding: 16px 18px 4px;
  border: 1px solid #DFEAF0;
  border-radius: 10px;
  background: #F9FCFD;
}

.content-language-settings {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
}

.language-setting-label {
  margin-right: 10px;
  color: #3E5260;
  font-size: 13px;
  font-weight: 600;
}

.public-language-setting {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.content-language-tip {
  margin: 9px 0 2px;
  color: #7B8C98;
  font-size: 12px;
}

.content-language-tabs {
  margin-top: 6px;
}

.translation-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.translation-alert {
  margin-bottom: 14px;
}

.upload-tip {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}

.upload-preview {
  margin-top: 12px;
}

.cover-preview {
  width: 180px;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.boat-thumbnail {
  width: 150px;
  min-height: 120px;
  padding: 12px 18px;
  border: 1px solid #d9e5ef;
  border-radius: 8px;
  box-sizing: border-box;
  background: #f8fbfd;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.boat-thumbnail:hover {
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.12);
}

.boat-thumbnail-row {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin: 6px 0;
}

.boat-thumbnail-seat,
.layout-seat {
  display: block;
  width: 24px;
  height: 18px;
  border: 1px solid #91caff;
  border-radius: 5px 5px 3px 3px;
  background: #e6f4ff;
}

.boat-thumbnail-summary {
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
}

.layout-editor {
  padding: 4px 8px;
}

.layout-editor-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #fafcff;
}

.layout-row-index {
  width: 54px;
  color: #64748b;
  font-size: 13px;
}

.layout-seat-preview {
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 76px;
}

.layout-delete {
  margin-left: auto;
  padding: 0;
  color: #ef4444;
}

.layout-summary {
  margin-top: 12px;
  color: #64748b;
  font-size: 13px;
  text-align: center;
}

/* ==================== 高级卡片式布局样式 ==================== */
.event-card {
  background: #ffffff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  flex-direction: column;
  height: 370px;
  min-height: 370px;
}

.event-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(24, 144, 255, 0.08);
  border-color: #bae7ff;
}

.event-card-header {
  flex: 0 0 auto;
  margin-bottom: 16px;
}

.event-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
  min-width: 0;
}

.event-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.4;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  width: 0;
}

.event-status-tag {
  flex-shrink: 0;
  margin: 0;
  padding: 2px 8px;
  font-weight: 500;
  border-radius: 4px;
}

.event-badges {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.display-setting {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-label {
  color: #475569;
}

.event-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 44px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
  min-width: 0;
}

.meta-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-item i, .meta-item .anticon {
  color: #9ca3af;
}

/* 中间统计数据网格 */
.event-card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px 6px;
  margin-bottom: 20px;
  border: 1px solid #f1f5f9;
  flex: 0 0 auto;
}

.stat-box {
  text-align: center;
  border-right: 1px solid #e2e8f0;
}

.stat-box:last-child {
  border-right: none;
}

.stat-val {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* 底部按钮栏 */
.event-card-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
  margin-top: auto;
}

.action-btn-main {
  width: 100%;
  height: 38px;
  font-weight: 600;
}

.event-card-secondary-actions {
  display: flex;
  gap: 6px;
}

.secondary-action-wrap,
.action-btn-share,
.action-btn-edit,
.action-btn-delete {
  flex: 1;
}

.secondary-action-wrap .action-btn-share {
  width: 100%;
}

.action-btn-share {
  height: 34px;
  padding: 0 10px;
  color: #287F79;
  border-color: #B9DAD7;
  font-size: 13px;
}

.action-btn-share:hover {
  color: #17645F;
  border-color: #69BEB7;
  background: #F4FBFA;
}

.action-btn-edit {
  height: 34px;
  padding: 0 10px;
  color: #475569;
  border-color: #cbd5e1;
  font-size: 13px;
}

.action-btn-edit:hover {
  color: #287F79;
  border-color: #69BEB7;
  background: #F4FBFA;
}

.action-btn-delete {
  height: 34px;
  color: #ef4444;
  border-color: #fecaca;
  padding: 0 9px;
  font-size: 13px;
}

.share-code-panel {
  text-align: center;
}

.share-code-title {
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
}

.share-code-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 260px;
  height: 260px;
  margin: 0 auto 16px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}

.share-code-image {
  display: block;
  width: 240px;
  height: 240px;
}

.share-code-tip {
  margin-bottom: 18px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

.action-btn-delete:hover {
  color: #dc2626;
  border-color: #dc2626;
}

.pagination-container {
  margin-top: 32px;
  text-align: right;
}
</style>
